import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  Divider,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { generateDailySaleSummaryReport } from '../services/reportService';
import { useStoreInfo } from '../../../hooks/useCompanyInfo';
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';
import { useAuth } from '../../../contexts/AuthContext';
import { getAllStores } from '../../companysettings/services/storeUserService';
import jsPDF from 'jspdf';

// Helper function to format dates
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateShort = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

// Helper function to format date as YYYY-MM-DD without timezone issues
const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get initial filters
const getInitialFilters = () => {
  const now = new Date();
  return {
    startDate: toLocalDateString(now),
    endDate: toLocalDateString(now),
    filterType: 'date', // 'date' or 'month'
    documentType: 'all',
    paymentMethod: 'all',
    storeId: 'all', // Default to 'All Stores'
  };
};

// Helper function to get quarter dates
const getQuarterDates = (quarter, year) => {
  const quarterStartMonth = (quarter - 1) * 3;
  const startDate = new Date(year, quarterStartMonth, 1);
  const endDate = new Date(year, quarterStartMonth + 3, 0);
  return {
    startDate: toLocalDateString(startDate),
    endDate: toLocalDateString(endDate),
  };
};

// Helper function to get available quarters (don't show future quarters)
const getAvailableQuarters = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-11
  const currentQuarter = Math.floor(currentMonth / 3) + 1; // 1-4
  
  const quarters = [];
  for (let q = 1; q <= currentQuarter; q++) {
    quarters.push({ quarter: q, year: currentYear });
  }
  return quarters;
};

const DailySaleSummaryReportViewer = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isCompanyAdmin, isSuperAdmin } = useAuth();
  const { storeInfo } = useStoreInfo();
  const paymentMethodsFromStore = useSelector((state) => state.paymentMethod.paymentMethods) || [];

  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [filters, setFilters] = useState(getInitialFilters);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [selectedStoreInfo, setSelectedStoreInfo] = useState(null); // Track selected store info

  // Show store dropdown for SuperAdmin and CompanyAdmin
  const canSelectStore = isCompanyAdmin;

  // Use selected store info if available, otherwise use default storeInfo
  const effectiveStoreInfo = selectedStoreInfo || storeInfo;

  // Fetch payment methods and stores on first open
  useEffect(() => {
    if (open && paymentMethodsFromStore.length === 0) {
      dispatch(fetchPaymentMethods());
    }
    
    // Fetch stores for SuperAdmin and CompanyAdmin users
    if (open && canSelectStore && stores.length === 0) {
      const fetchStores = async () => {
        setLoadingStores(true);
        try {
          const response = await getAllStores();
          const storesList = response?.payload || [];
          setStores(storesList);
        } catch (error) {
          console.error('Failed to fetch stores:', error);
          toast.error('Failed to load stores');
        } finally {
          setLoadingStores(false);
        }
      };
      fetchStores();
    }
  }, [open, dispatch, paymentMethodsFromStore.length, canSelectStore, stores.length]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setFilters(getInitialFilters());
      setReportData(null);
      setFiltersExpanded(true);
    }
  }, [open]);

  const handleFilterChange = (field) => (event) => {
    let value = event.target.value;
    
    // Convert storeId to number or null
    if (field === 'storeId') {
      // Convert 'all' or empty string to null, otherwise to number
      value = (value === 'all' || value === '') ? null : Number(value);
      
      // Update selected store info when store changes
      if (value === null) {
        setSelectedStoreInfo(null); // Reset to default store
      } else {
        const selectedStore = stores.find(store => store.Id === value);
        if (selectedStore) {
          // Map store data to storeInfo format
          setSelectedStoreInfo({
            name: selectedStore.CompanyName || selectedStore.Name,
            storeName: selectedStore.StoreName || selectedStore.Name,
            address: selectedStore.Address || selectedStore.Street || selectedStore.StreetAddress,
            city: selectedStore.City,
            postCode: selectedStore.PostCode || selectedStore.PostalCode || selectedStore.ZipCode,
            phone: selectedStore.Phone || selectedStore.PhoneNumber,
            email: selectedStore.Email,
            vat: selectedStore.VATNumber || selectedStore.VAT,
          });
        }
      }
    }
    
    setFilters({ ...filters, [field]: value });
  };

  const handleQuickFilter = (type, value) => {
    const now = new Date();
    let startDate, endDate;

    switch (type) {
      case 'today':
        startDate = toLocalDateString(now);
        endDate = toLocalDateString(now);
        break;

      case 'yesterday': {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = toLocalDateString(yesterday);
        endDate = toLocalDateString(yesterday);
        break;
      }
      
      case 'month':
        const targetDate = new Date(now.getFullYear(), now.getMonth() + value, 1);
        const monthStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
        const monthEnd = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
        startDate = toLocalDateString(monthStart);
        endDate = toLocalDateString(monthEnd);
        break;
      
      case 'quarter':
        const quarterDates = getQuarterDates(value, now.getFullYear());
        startDate = quarterDates.startDate;
        endDate = quarterDates.endDate;
        break;
      
      default:
        return;
    }
    
    setFilters({
      ...filters,
      startDate,
      endDate,
      filterType: type,
    });
  };

  const handleGenerate = async () => {
    if (!filters.startDate || !filters.endDate) {
      toast.error(t('please_select_dates') || 'Please select start and end dates');
      return;
    }

    if (new Date(filters.startDate) > new Date(filters.endDate)) {
      toast.error(t('start_date_before_end') || 'Start date must be before end date');
      return;
    }

    try {
      setLoading(true);
      const data = await generateDailySaleSummaryReport(filters);
      setReportData(data);
      
      setFiltersExpanded(false);
      toast.success(t('report_generated') || 'Report generated successfully!');
    } catch (error) {

      toast.error(error.response?.data?.message || t('report_generation_failed') || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handlePDF = useCallback(async () => {
    if (!reportData || !effectiveStoreInfo) return;
    
    setPdfLoading(true);
    try {
      const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;
      let yPos = margin;

      // ─── Header: Report Title (Left) & Store Info (Right) ───
      const midPoint = pageWidth / 2;

      // Left: Report Title and Metadata
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(t('daily_sale_summary_report') || 'Daily Sale Summary Report', margin, yPos);
      yPos += 8;

      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text(`${t('period') || 'Period'}: ${formatDate(filters.startDate)} - ${formatDate(filters.endDate)}`, margin, yPos);
      yPos += 4;
      
      // Show filter information (always show) - split into 2 lines
      const docTypeLabel = filters.documentType === 'all' 
        ? t('all') || 'All'
        : filters.documentType === 'ticket_de_caisse' 
        ? t('ticket_de_caisse') || 'Ticket de Caisse'
        : filters.documentType === 'invoice' 
        ? t('invoice') || 'Invoice (Facture)'
        : filters.documentType;
      doc.text(`${t('document_type') || 'Document Type'}: ${docTypeLabel}`, margin, yPos);
      yPos += 4;
      
      const paymentMethodLabel = filters.paymentMethod === 'all' ? t('all') || 'All' : filters.paymentMethod;
      doc.text(`${t('payment_method') || 'Payment Method'}: ${paymentMethodLabel}`, margin, yPos);
      yPos += 4;
      
      doc.text(`${t('generated_on') || 'Generated on'}: ${new Date().toLocaleString('en-US')}`, margin, yPos);

      // Right: Store Info
      let storeYPos = margin;
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(effectiveStoreInfo.name || 'Company Name', pageWidth - margin, storeYPos, { align: 'right' });
      storeYPos += 5;
      
      if (effectiveStoreInfo.storeName) {
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(60, 60, 60);
        doc.text(effectiveStoreInfo.storeName, pageWidth - margin, storeYPos, { align: 'right' });
        storeYPos += 6;
      } else {
        storeYPos += 1;
      }

      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      if (effectiveStoreInfo.address) {
        const addr = `${effectiveStoreInfo.address}${effectiveStoreInfo.city ? ', ' + effectiveStoreInfo.city : ''}${effectiveStoreInfo.postCode ? ' ' + effectiveStoreInfo.postCode : ''}`;
        doc.text(addr, pageWidth - margin, storeYPos, { align: 'right' });
        storeYPos += 4;
      }
      if (effectiveStoreInfo.phone || effectiveStoreInfo.email) {
        if (effectiveStoreInfo.phone) {
          doc.text(effectiveStoreInfo.phone, pageWidth - margin, storeYPos, { align: 'right' });
          storeYPos += 4;
        }
        if (effectiveStoreInfo.email) {
          doc.text(effectiveStoreInfo.email, pageWidth - margin, storeYPos, { align: 'right' });
          storeYPos += 4;
        }
      }
      if (effectiveStoreInfo.vat) {
        doc.text(`VAT: ${effectiveStoreInfo.vat}`, pageWidth - margin, storeYPos, { align: 'right' });
      }

      yPos += 8;
      doc.setDrawColor(220, 220, 220);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      // ─── Summary Cards ───
      const summary = reportData.summary || {};
      
      // Calculate total documents
      const totalDocs = (reportData.dailySales || []).reduce((sum, day) => {
        const invoiceCount = day.InvoiceCount || day.invoiceCount || 0;
        const ticketCount = day.TicketCount || day.ticketCount || 0;
        return sum + invoiceCount + ticketCount;
      }, 0);
      
      const cardData = [
        { label: t('total_sales_incl') || 'Total Sales (Incl. VAT)', value: `€${Number(summary.totalSalesInclVAT || 0).toFixed(2)}`, color: [76, 175, 80] },
        { label: t('total_vat') || 'Total VAT', value: `€${Number(summary.totalVAT || 0).toFixed(2)}`, color: [33, 150, 243] },
        { label: t('total_sales_excl') || 'Total Sales (Excl. VAT)', value: `€${Number(summary.totalSalesExclVAT || 0).toFixed(2)}`, color: [255, 152, 0] },
        { label: t('total_documents') || 'Total Documents', value: String(totalDocs), color: [156, 39, 176] },
      ];

      // Payment breakdown cards
      const paymentCards = summary.paymentBreakdownSummary || [];

      const cardWidth = (contentWidth - 15) / 4; // 4 cards in one row
      const cardHeight = 18;
      let cardX = margin;
      const cardY = yPos;

      cardData.forEach((card, index) => {
        // Card background
        doc.setFillColor(card.color[0], card.color[1], card.color[2]);
        doc.setDrawColor(card.color[0], card.color[1], card.color[2]);
        doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 2, 2, 'FD');

        // Card text
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.text(card.label, cardX + 2, cardY + 5, { maxWidth: cardWidth - 4 });

        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(card.value, cardX + 2, cardY + 14);

        cardX += cardWidth + 5;
      });

      yPos = cardY + cardHeight + 12;

      // ─── Payment Breakdown Summary Cards ───
      if (paymentCards.length > 0) {
        yPos += 5;
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(30, 58, 95);
        doc.text(t('payment_breakdown_summary') || 'Payment Breakdown Summary', margin, yPos);
        yPos += 8;

        const pmColors = [
          [244, 67, 54],   // Red
          [0, 188, 212],   // Cyan
          [255, 152, 0],   // Orange
          [76, 175, 80],   // Green
          [156, 39, 176],  // Purple
          [63, 81, 181],   // Indigo
          [0, 150, 136],   // Teal
          [255, 87, 34],   // Deep Orange
        ];

        const pmCardsPerRow = 4; // Always use 4 cards per row to match summary cards
        const pmCardWidth = (contentWidth - 15) / 4; // Same calculation as summary cards
        const pmCardHeight = 18; // Match summary card height
        let pmCardX = margin;
        const pmCardY = yPos;
        
        paymentCards.forEach((pm, idx) => {
          if (idx > 0 && idx % pmCardsPerRow === 0) {
            yPos += pmCardHeight + 3;
            pmCardX = margin;
          }

          const pmColor = pmColors[idx % pmColors.length];
          
          // Card background
          doc.setFillColor(pmColor[0], pmColor[1], pmColor[2]);
          doc.setDrawColor(pmColor[0], pmColor[1], pmColor[2]);
          doc.roundedRect(pmCardX, pmCardY + (Math.floor(idx / pmCardsPerRow) * (pmCardHeight + 3)), pmCardWidth, pmCardHeight, 2, 2, 'FD');
          
          const cardYPos = pmCardY + (Math.floor(idx / pmCardsPerRow) * (pmCardHeight + 3));
          
          // Card content
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(7);
          doc.setFont(undefined, 'bold');
          doc.text(pm.PaymentMethod, pmCardX + 2, cardYPos + 5);
          
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');
          doc.text(`€${Number(pm.Amount || 0).toFixed(2)}`, pmCardX + 2, cardYPos + 12);
          
          doc.setFontSize(6);
          doc.setFont(undefined, 'normal');
          doc.text(`${pm.TransactionCount || 0} ${t('transactions') || 'trans.'}`, pmCardX + 2, cardYPos + 16);
          
          // Percentage badge
          const badgeWidth = 15;
          const badgeX = pmCardX + pmCardWidth - badgeWidth - 2;
          doc.setFillColor(255, 255, 255, 0.3);
          doc.roundedRect(badgeX, cardYPos + 2, badgeWidth, 4, 1, 1, 'F');
          doc.setFillColor(pmColor[0], pmColor[1], pmColor[2]);
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(6.5);
          doc.setFont(undefined, 'bold');
          doc.text(`${Number(pm.Percentage || 0).toFixed(1)}%`, badgeX + badgeWidth / 2, cardYPos + 4.5, { align: 'center' });
          
          pmCardX += pmCardWidth + 5;
        });
        
        yPos = pmCardY + (Math.ceil(paymentCards.length / pmCardsPerRow) * (pmCardHeight + 3)) + 5;
      }

      yPos += 10;

      // ─── Daily Sales Table ───
      const tableHeaders = [
        { text: t('date') || 'Date', width: 26, align: 'left' },
        { text: t('num_documents') || 'Docs', width: 26, align: 'center' },
        { text: t('total_excl_vat') || 'Total (Excl. VAT)', width: 26, align: 'right' },
        { text: t('total_vat') || 'Total VAT', width: 22, align: 'right' },
        { text: t('total_incl_vat') || 'Total (Incl. VAT)', width: 26, align: 'right' },
        { text: t('payment_methods') || 'Payment Methods', width: 54, align: 'left' },
      ];

      const tableData = (reportData.dailySales || []).map(day => {
        const paymentBreakdown = day.PaymentBreakdown || day.paymentBreakdown || [];
        const paymentText = paymentBreakdown.length > 0 
          ? paymentBreakdown.map(pm => `${pm.PaymentMethod}: €${Number(pm.Amount || 0).toFixed(2)}`).join('\n')
          : '-';
        const invoiceCount = day.InvoiceCount || day.invoiceCount || 0;
        const ticketCount = day.TicketCount || day.ticketCount || 0;
        const totalDocs = invoiceCount + ticketCount;
        // Format document text on two lines: total on first line, breakdown on second
        const docText = totalDocs > 0 ? `${totalDocs}\n${t('invoices') || 'I'}:${invoiceCount} ${t('tickets') || 'T'}:${ticketCount}` : '0';
        return [
          { text: formatDate(day.Date || day.date), align: 'left' },
          { text: docText, align: 'center' },
          { text: `€${Number(day.TotalExclVAT || day.totalExclVAT || 0).toFixed(2)}`, align: 'right' },
          { text: `€${Number(day.TotalVAT || day.totalVAT || 0).toFixed(2)}`, align: 'right' },
          { text: `€${Number(day.TotalInclVAT || day.totalInclVAT || 0).toFixed(2)}`, align: 'right' },
          { text: paymentText, align: 'left' },
        ];
      });

      // Draw table header
      doc.setFillColor(30, 58, 95);
      doc.rect(margin, yPos, contentWidth, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');

      let xPos = margin;
      tableHeaders.forEach((header) => {
        const textX = header.align === 'right' 
          ? xPos + header.width - 2 
          : header.align === 'center' 
          ? xPos + header.width / 2 
          : xPos + 2;
        doc.text(header.text, textX, yPos + 5.5, { align: header.align });
        xPos += header.width;
      });
      
      // Draw vertical lines in header
      xPos = margin;
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.3);
      tableHeaders.forEach((header, idx) => {
        if (idx < tableHeaders.length - 1) {
          xPos += header.width;
          doc.line(xPos, yPos, xPos, yPos + 8);
        }
      });
      
      // Draw header borders (top, left, right)
      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, margin + contentWidth, yPos); // Top border
      doc.line(margin, yPos, margin, yPos + 8); // Left border
      doc.line(margin + contentWidth, yPos, margin + contentWidth, yPos + 8); // Right border
      doc.line(margin, yPos + 8, margin + contentWidth, yPos + 8); // Bottom border
      doc.setLineWidth(0.1);
      
      yPos += 8;

      // Draw table rows
      doc.setFont(undefined, 'normal');
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(8);

      tableData.forEach((rowData, rowIndex) => {
        // Check if this is a zero-sale day
        const dayData = reportData.dailySales[rowIndex];
        const isZeroSale = (dayData.ItemsSold || dayData.itemsSold || 0) === 0 || 
                          Number(dayData.TotalInclVAT || dayData.totalInclVAT || 0) === 0;

        // Calculate row height based on payment methods and documents content
        const paymentMethodsCell = rowData[5];
        const documentsCell = rowData[1];
        doc.setFontSize(7);
        
        // Calculate lines for payment methods
        const paymentLines = doc.splitTextToSize(paymentMethodsCell.text, tableHeaders[5].width - 4);
        const paymentLineCount = Array.isArray(paymentLines) ? paymentLines.length : 1;
        
        // Calculate lines for documents (usually 2 lines if there are documents)
        const docLines = doc.splitTextToSize(documentsCell.text, tableHeaders[1].width - 4);
        const docLineCount = Array.isArray(docLines) ? docLines.length : 1;
        
        // Use the maximum line count to determine row height
        const maxLineCount = Math.max(paymentLineCount, docLineCount);
        const rowHeight = Math.max(7, 3 + (maxLineCount * 3.5)); // Dynamic height based on content
        doc.setFontSize(8);

        // Row background color
        if (isZeroSale) {
          // Light red for zero sales days
          doc.setFillColor(255, 235, 238);
          doc.rect(margin, yPos, contentWidth, rowHeight, 'F');
        } else if (rowIndex % 2 === 0) {
          // Alternate row background
          doc.setFillColor(248, 250, 252);
          doc.rect(margin, yPos, contentWidth, rowHeight, 'F');
        }

        // Draw row cells
        xPos = margin;
        rowData.forEach((cell, cellIndex) => {
          const textX = cell.align === 'right' 
            ? xPos + tableHeaders[cellIndex].width - 2 
            : cell.align === 'center' 
            ? xPos + tableHeaders[cellIndex].width / 2 
            : xPos + 2;
          
          // For payment methods column, use smaller font and wrap text
          if (cellIndex === 5) {
            doc.setFontSize(7);
            const lines = doc.splitTextToSize(cell.text, tableHeaders[cellIndex].width - 4);
            doc.text(lines, xPos + 2, yPos + 3.5);
            doc.setFontSize(8);
          } else if (cellIndex === 1) {
            // For documents column, handle multi-line text
            doc.setFontSize(7);
            const lines = doc.splitTextToSize(cell.text, tableHeaders[cellIndex].width - 4);
            const textY = rowHeight <= 7 ? yPos + 3.5 : yPos + (rowHeight / 2) - 1;
            doc.text(lines, textX, textY, { align: cell.align });
            doc.setFontSize(8);
          } else {
            // Center text vertically in the row
            const textY = rowHeight <= 7 ? yPos + 5 : yPos + (rowHeight / 2) + 1;
            doc.text(cell.text, textX, textY, { align: cell.align });
          }
          xPos += tableHeaders[cellIndex].width;
        });

        // Draw row border (bottom line)
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        doc.line(margin, yPos + rowHeight, margin + contentWidth, yPos + rowHeight);
        
        // Draw left and right borders
        doc.setDrawColor(180, 180, 180);
        doc.line(margin, yPos, margin, yPos + rowHeight); // Left border
        doc.line(margin + contentWidth, yPos, margin + contentWidth, yPos + rowHeight); // Right border
        
        // Draw vertical grid lines for cleaner look
        xPos = margin;
        tableHeaders.forEach((header, idx) => {
          if (idx < tableHeaders.length - 1) { // Don't draw line after last column
            xPos += header.width;
            doc.setDrawColor(220, 220, 220);
            doc.line(xPos, yPos, xPos, yPos + rowHeight);
          }
        });
        
        yPos += rowHeight;

        // Check for page break
        if (yPos > pageHeight - 30) {
          doc.addPage();
          yPos = margin;
          
          // Redraw table header on new page
          doc.setFillColor(30, 58, 95);
          doc.rect(margin, yPos, contentWidth, 8, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(9);
          doc.setFont(undefined, 'bold');
          xPos = margin;
          tableHeaders.forEach((header) => {
            const textX = header.align === 'right' 
              ? xPos + header.width - 2 
              : header.align === 'center' 
              ? xPos + header.width / 2 
              : xPos + 2;
            doc.text(header.text, textX, yPos + 5.5, { align: header.align });
            xPos += header.width;
          });
          
          // Draw vertical lines in header
          xPos = margin;
          doc.setDrawColor(255, 255, 255);
          doc.setLineWidth(0.3);
          tableHeaders.forEach((header, idx) => {
            if (idx < tableHeaders.length - 1) {
              xPos += header.width;
              doc.line(xPos, yPos, xPos, yPos + 8);
            }
          });
          
          // Draw header borders
          doc.setDrawColor(100, 100, 100);
          doc.setLineWidth(0.5);
          doc.line(margin, yPos, margin + contentWidth, yPos); // Top
          doc.line(margin, yPos, margin, yPos + 8); // Left
          doc.line(margin + contentWidth, yPos, margin + contentWidth, yPos + 8); // Right
          doc.line(margin, yPos + 8, margin + contentWidth, yPos + 8); // Bottom
          doc.setLineWidth(0.1);
          
          yPos += 8;
          doc.setFont(undefined, 'normal');
          doc.setTextColor(50, 50, 50);
          doc.setFontSize(8);
        }
      });

      // ─── Totals Row ───
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, yPos, contentWidth, 9, 'F');
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);

      // Calculate totals
      const totals = {
        totalDocs: reportData.dailySales.reduce((sum, day) => {
          const invoiceCount = day.InvoiceCount || day.invoiceCount || 0;
          const ticketCount = day.TicketCount || day.ticketCount || 0;
          return sum + invoiceCount + ticketCount;
        }, 0),
        invoiceCount: reportData.dailySales.reduce((sum, day) => sum + (day.InvoiceCount || day.invoiceCount || 0), 0),
        ticketCount: reportData.dailySales.reduce((sum, day) => sum + (day.TicketCount || day.ticketCount || 0), 0),
        totalExclVAT: reportData.dailySales.reduce((sum, day) => sum + Number(day.TotalExclVAT || day.totalExclVAT || 0), 0),
        totalVAT: reportData.dailySales.reduce((sum, day) => sum + Number(day.TotalVAT || day.totalVAT || 0), 0),
        totalInclVAT: reportData.dailySales.reduce((sum, day) => sum + Number(day.TotalInclVAT || day.totalInclVAT || 0), 0),
      };

      xPos = margin;
      const totalsRow = [
        { text: t('total') || 'TOTAL', align: 'left' },
        { text: `${totals.totalDocs}\n${t('invoices') || 'I'}:${totals.invoiceCount} ${t('tickets') || 'T'}:${totals.ticketCount}`, align: 'center' },
        { text: `€${totals.totalExclVAT.toFixed(2)}`, align: 'right' },
        { text: `€${totals.totalVAT.toFixed(2)}`, align: 'right' },
        { text: `€${totals.totalInclVAT.toFixed(2)}`, align: 'right' },
        { text: '', align: 'left' }, // Empty payment methods column
      ];

      totalsRow.forEach((cell, cellIndex) => {
        const textX = cell.align === 'right' 
          ? xPos + tableHeaders[cellIndex].width - 2 
          : cell.align === 'center' 
          ? xPos + tableHeaders[cellIndex].width / 2 
          : xPos + 2;
        // Handle multi-line text for documents column in totals
        if (cellIndex === 1) {
          doc.setFontSize(9);
          const lines = doc.splitTextToSize(cell.text, tableHeaders[cellIndex].width - 4);
          doc.text(lines, textX, yPos + 4, { align: cell.align });
          doc.setFontSize(10);
        } else {
          doc.text(cell.text, textX, yPos + 6, { align: cell.align });
        }
        xPos += tableHeaders[cellIndex].width;
      });
      
      // Draw vertical lines in totals row
      xPos = margin;
      doc.setDrawColor(200, 200, 200);
      tableHeaders.forEach((header, idx) => {
        if (idx < tableHeaders.length - 1) {
          xPos += header.width;
          doc.line(xPos, yPos, xPos, yPos + 9);
        }
      });
      
      // Draw borders of totals row
      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, margin, yPos + 9); // Left border
      doc.line(margin + contentWidth, yPos, margin + contentWidth, yPos + 9); // Right border
      doc.line(margin, yPos + 9, margin + contentWidth, yPos + 9); // Bottom border
      doc.setLineWidth(0.1);
      
      yPos += 9;

      // ─── Footer ───
      if (yPos + 20 < pageHeight - margin) {
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.setFont(undefined, 'normal');
        doc.text('Generated by Billeur • Confidential', margin, pageHeight - 10);
        doc.text(new Date().toLocaleString('en-US'), pageWidth - margin - 40, pageHeight - 10);
      }

      // Save PDF
      const companyName = (effectiveStoreInfo.name || 'Company').replace(/[^a-zA-Z0-9]/g, '-');
      const storeName = (effectiveStoreInfo.storeName || 'Store').replace(/[^a-zA-Z0-9]/g, '-');
      const dateRange = `${filters.startDate}-to-${filters.endDate}`;
      const filename = `${companyName}-${storeName}-daily-sale-summary-${dateRange}.pdf`;
      doc.save(filename);
      toast.success(t('pdf_exported_success') || 'PDF exported successfully!');
    } catch (err) {

      toast.error(t('pdf_export_failed') || 'Failed to export PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  }, [reportData, filters, effectiveStoreInfo, t]);

  const handlePrint = useCallback(() => {
    if (!reportData) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error(t('popup_blocked') || 'Please allow popups to print');
      return;
    }

    const summary = reportData.summary || {};
    const dailySales = reportData.dailySales || [];

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Daily Sale Summary Report</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; padding: 20px; color: #333; }
          .header { margin-bottom: 20px; border-bottom: 2px solid #e0e0e0; padding-bottom: 15px; }
          .header h1 { font-size: 24px; color: #1976d2; margin-bottom: 8px; }
          .header .meta { font-size: 11px; color: #666; line-height: 1.6; }
          .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 25px; }
          .summary-card { padding: 12px; border-radius: 8px; color: white; }
          .summary-card:nth-child(1) { background: linear-gradient(135deg, #4caf50 0%, #81c784 100%); }
          .summary-card:nth-child(2) { background: linear-gradient(135deg, #2196f3 0%, #64b5f6 100%); }
          .summary-card:nth-child(3) { background: linear-gradient(135deg, #ff9800 0%, #ffb74d 100%); }
          .summary-card:nth-child(4) { background: linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%); }
          .summary-card .label { font-size: 9px; font-weight: 600; text-transform: uppercase; margin-bottom: 5px; opacity: 0.95; }
          .summary-card .value { font-size: 18px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #1e3a5f; color: white; padding: 10px; text-align: left; font-size: 11px; font-weight: 600; }
          th.right { text-align: right; }
          td { padding: 8px 10px; border-bottom: 1px solid #e0e0e0; font-size: 11px; }
          td.right { text-align: right; }
          tbody tr:nth-child(even) { background: #f8f8f8; }
          tbody tr.zero-sale { background: #ffebee !important; }
          .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #e0e0e0; font-size: 10px; color: #999; text-align: center; }
          @media print {
            body { padding: 10mm; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${t('daily_sale_summary_report') || 'Daily Sale Summary Report'}</h1>
          <div class="meta">
            <div><strong>Period:</strong> ${formatDate(filters.startDate)} - ${formatDate(filters.endDate)}</div>
            <div><strong>Generated on:</strong> ${new Date().toLocaleString('en-US')}</div>
            ${effectiveStoreInfo?.name ? `<div><strong>Company:</strong> ${effectiveStoreInfo.name}</div>` : ''}
            ${effectiveStoreInfo?.storeName ? `<div><strong>Store:</strong> ${effectiveStoreInfo.storeName}</div>` : ''}
          </div>
        </div>

        <div class="summary">
          <div class="summary-card">
            <div class="label">${t('total_sales_incl') || 'Total Sales (Incl. VAT)'}</div>
            <div class="value">€${Number(summary.totalSalesInclVAT || 0).toFixed(2)}</div>
          </div>
          <div class="summary-card">
            <div class="label">${t('total_vat') || 'Total VAT'}</div>
            <div class="value">€${Number(summary.totalVAT || 0).toFixed(2)}</div>
          </div>
          <div class="summary-card">
            <div class="label">${t('total_sales_excl') || 'Total Sales (Excl. VAT)'}</div>
            <div class="value">€${Number(summary.totalSalesExclVAT || 0).toFixed(2)}</div>
          </div>
          <div class="summary-card">
            <div class="label">${t('total_documents') || 'Total Documents'}</div>
            <div class="value">${dailySales.reduce((sum, day) => {
              const invoiceCount = day.InvoiceCount || day.invoiceCount || 0;
              const ticketCount = day.TicketCount || day.ticketCount || 0;
              return sum + invoiceCount + ticketCount;
            }, 0)}</div>
          </div>
        </div>

        ${reportData.summary?.paymentBreakdownSummary && reportData.summary.paymentBreakdownSummary.length > 0 ? `
        <div style="margin-top: 20px; margin-bottom: 25px;">
          <h3 style="font-size: 14px; color: #1e3a5f; margin-bottom: 10px;">${t('payment_breakdown_summary') || 'Payment Breakdown Summary'}</h3>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px;">
            ${reportData.summary.paymentBreakdownSummary.map((pm, index) => {
              const colors = [
                ['#f44336', '#e57373'],
                ['#00bcd4', '#4dd0e1'],
                ['#ff9800', '#ffb74d'],
                ['#4caf50', '#81c784'],
                ['#9c27b0', '#ba68c8'],
                ['#3f51b5', '#7986cb'],
                ['#009688', '#4db6ac'],
                ['#ff5722', '#ff8a65'],
              ];
              const colorPair = colors[index % colors.length];
              return `
                <div style="padding: 12px; border-radius: 8px; background: linear-gradient(135deg, ${colorPair[0]} 0%, ${colorPair[1]} 100%); color: white;">
                  <div style="font-size: 9px; font-weight: 600; text-transform: uppercase; margin-bottom: 5px; opacity: 0.95;">${pm.PaymentMethod}</div>
                  <div style="font-size: 18px; font-weight: bold; margin-bottom: 5px;">€${Number(pm.Amount || 0).toFixed(2)}</div>
                  <div style="display: flex; justify-content: space-between; align-items: center; font-size: 10px;">
                    <span>${pm.TransactionCount || 0} transactions</span>
                    <span style="background: rgba(255,255,255,0.25); padding: 2px 6px; border-radius: 10px; font-weight: 700;">${Number(pm.Percentage || 0).toFixed(1)}%</span>
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
        ` : ''}

        <table>
          <thead>
            <tr>
              <th>${t('date') || 'Date'}</th>
              <th class="right">${t('num_documents') || 'Documents'}</th>
              <th class="right">${t('total_excl_vat') || 'Total (Excl. VAT)'}</th>
              <th class="right">${t('total_vat') || 'Total VAT'}</th>
              <th class="right">${t('total_incl_vat') || 'Total (Incl. VAT)'}</th>
              <th>${t('payment_methods') || 'Payment Methods'}</th>
            </tr>
          </thead>
          <tbody>
            ${dailySales.map(day => {
              const invoiceCount = day.InvoiceCount || day.invoiceCount || 0;
              const ticketCount = day.TicketCount || day.ticketCount || 0;
              const totalDocs = invoiceCount + ticketCount;
              const isZeroSale = totalDocs === 0 || Number(day.TotalInclVAT || day.totalInclVAT || 0) === 0;
              const paymentBreakdown = day.PaymentBreakdown || day.paymentBreakdown || [];
              const paymentText = paymentBreakdown.length > 0
                ? paymentBreakdown.map(pm => `<div style="background: #f0f2ff; padding: 2px 6px; margin-bottom: 2px; border-radius: 4px; font-size: 9px; color: #667eea; font-weight: 600;">${pm.PaymentMethod}: €${Number(pm.Amount || 0).toFixed(2)}</div>`).join('')
                : '-';
              
              return `
              <tr${isZeroSale ? ' class="zero-sale"' : ''}>
                <td>${formatDate(day.Date || day.date)}</td>
                <td class="right">${totalDocs > 0 ? `${totalDocs} (Invoice(s):${invoiceCount} Ticket(s):${ticketCount})` : '0'}</td>
                <td class="right">€${Number(day.TotalExclVAT || day.totalExclVAT || 0).toFixed(2)}</td>
                <td class="right">€${Number(day.TotalVAT || day.totalVAT || 0).toFixed(2)}</td>
                <td class="right">€${Number(day.TotalInclVAT || day.totalInclVAT || 0).toFixed(2)}</td>
                <td>${paymentText}</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>

        <div class="footer">
          Generated by Billeur • Confidential • ${new Date().toLocaleString('en-US')}
        </div>

        <script>
          window.onload = () => { window.print(); };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  }, [reportData, filters, effectiveStoreInfo, t]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!reportData?.dailySales) return null;
    
    return reportData.dailySales.reduce((acc, day) => {
      const invoiceCount = day.InvoiceCount || day.invoiceCount || 0;
      const ticketCount = day.TicketCount || day.ticketCount || 0;
      return {
        totalDocs: acc.totalDocs + invoiceCount + ticketCount,
        invoiceCount: acc.invoiceCount + invoiceCount,
        ticketCount: acc.ticketCount + ticketCount,
        totalExclVAT: acc.totalExclVAT + Number(day.totalExclVAT || day.TotalExclVAT || 0),
        totalVAT: acc.totalVAT + Number(day.totalVAT || day.TotalVAT || 0),
        totalInclVAT: acc.totalInclVAT + Number(day.totalInclVAT || day.TotalInclVAT || 0),
      };
    }, { totalDocs: 0, invoiceCount: 0, ticketCount: 0, totalExclVAT: 0, totalVAT: 0, totalInclVAT: 0 });
  }, [reportData]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
      PaperProps={{
        sx: {
          maxHeight: '95vh',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AssessmentIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {t('daily_sale_summary_report') || 'Daily Sale Summary Report'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {t('daily_sale_summary_desc') || 'View daily sales breakdown for selected period'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* Filters Section */}
        <Paper sx={{ mb: 3, overflow: 'hidden' }} elevation={3}>
          <Box
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon />
              <Typography variant="h6" fontWeight={700}>
                {t('filters') || 'Report Filters'}
              </Typography>
            </Box>
            <IconButton size="small" sx={{ color: 'white' }}>
              {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={filtersExpanded}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                {/* Quick Date Selectors */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleQuickFilter('today', null)}
                      startIcon={<CalendarIcon />}
                    >
                      {t('today') || 'Today'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleQuickFilter('yesterday', null)}
                    >
                      {t('yesterday') || 'Yesterday'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleQuickFilter('month', 0)}
                    >
                      {t('current_month') || 'Current Month'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleQuickFilter('month', -1)}
                    >
                      {t('last_month') || 'Last Month'}
                    </Button>
                    {getAvailableQuarters().map(({ quarter, year }) => (
                      <Button
                        key={`Q${quarter}-${year}`}
                        size="small"
                        variant="outlined"
                        onClick={() => handleQuickFilter('quarter', quarter)}
                      >
                        Q{quarter}
                      </Button>
                    ))}
                  </Box>
                </Grid>

                  {/* Date Range */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label={t('start_date') || 'Start Date'}
                      value={filters.startDate}
                      onChange={handleFilterChange('startDate')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label={t('end_date') || 'End Date'}
                      value={filters.endDate}
                      onChange={handleFilterChange('endDate')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

{/* Store Selection - Only for SuperAdmin and CompanyAdmin */}
                  {canSelectStore && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" disabled={loadingStores}>
                        <InputLabel>{t('store') || 'Store'}</InputLabel>
                        <Select
                          value={filters.storeId}
                          onChange={handleFilterChange('storeId')}
                          label={t('store') || 'Store'}
                          displayEmpty
                        >
                          <MenuItem value="all">{t('all_stores') || 'All Stores'}</MenuItem>
                          {stores.map((store) => (
                            <MenuItem key={store.Id} value={store.Id}>
                              {store.StoreName || store.Name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {/* Document Type */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('document_type') || 'Document Type'}</InputLabel>
                      <Select
                        value={filters.documentType}
                        onChange={handleFilterChange('documentType')}
                        label={t('document_type') || 'Document Type'}
                      >
                        <MenuItem value="all">{t('all_documents') || 'All Documents'}</MenuItem>
                        <MenuItem value="ticket_de_caisse">{t('ticket_de_caisse') || 'Ticket de Caisse'}</MenuItem>
                        <MenuItem value="invoice">{t('invoice') || 'Invoice (Facture)'}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Payment Method */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('payment_method') || 'Payment Method'}</InputLabel>
                      <Select
                        value={filters.paymentMethod}
                        onChange={handleFilterChange('paymentMethod')}
                        label={t('payment_method') || 'Payment Method'}
                      >
                        <MenuItem value="all">{t('all_methods') || 'All Methods'}</MenuItem>
                        {paymentMethodsFromStore.map((method) => (
                          <MenuItem key={method.id} value={method.name || method.label}>
                            {method.label || method.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Generate Button */}
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={loading ? <RefreshIcon className="spin" /> : <TrendingUpIcon />}
                      onClick={handleGenerate}
                      disabled={loading}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        },
                      }}
                    >
                      {loading ? (t('generating') || 'Generating...') : (t('generate_report') || 'Generate Report')}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Paper>

          {/* Report Content */}
          {reportData && (
            <Box>
              {/* Active Filters */}
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {t('active_filters') || 'Active Filters:'}
                </Typography>
                <Chip
                  label={`${formatDate(filters.startDate)} - ${formatDate(filters.endDate)}`}
                  size="small"
                  icon={<CalendarIcon />}
                />
              </Box>

              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                      color: 'white',
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {t('total_sales_incl') || 'Total Sales (Incl. VAT)'}
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      €{Number(reportData.summary?.totalSalesInclVAT || 0).toFixed(2)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
                      color: 'white',
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {t('total_vat') || 'Total VAT'}
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      €{Number(reportData.summary?.totalVAT || 0).toFixed(2)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                      color: 'white',
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {t('total_sales_excl') || 'Total Sales (Excl. VAT)'}
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      €{Number(reportData.summary?.totalSalesExclVAT || 0).toFixed(2)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
                      color: 'white',
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {t('total_documents') || 'Total Documents'}
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {totals?.totalDocs || 0}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Payment Breakdown Summary - Cards */}
              {reportData.summary?.paymentBreakdownSummary && reportData.summary.paymentBreakdownSummary.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('payment_breakdown_summary') || 'Payment Breakdown Summary'}
                  </Typography>
                  <Grid container spacing={2}>
                    {reportData.summary.paymentBreakdownSummary.map((pm, index) => {
                      const colors = [
                        ['#f44336', '#e57373'], // Red
                        ['#00bcd4', '#4dd0e1'], // Cyan
                        ['#ff9800', '#ffb74d'], // Orange
                        ['#4caf50', '#81c784'], // Green
                        ['#9c27b0', '#ba68c8'], // Purple
                        ['#3f51b5', '#7986cb'], // Indigo
                        ['#009688', '#4db6ac'], // Teal
                        ['#ff5722', '#ff8a65'], // Deep Orange
                      ];
                      const colorPair = colors[index % colors.length];
                      return (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                          <Paper
                            elevation={3}
                            sx={{
                              p: 2,
                              background: `linear-gradient(135deg, ${colorPair[0]} 0%, ${colorPair[1]} 100%)`,
                              color: 'white',
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                opacity: 0.9,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                display: 'block',
                                mb: 1
                              }}
                            >
                              {pm.PaymentMethod}
                            </Typography>
                            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                              €{Number(pm.Amount || 0).toFixed(2)}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                              <Typography variant="caption" sx={{ opacity: 0.95 }}>
                                {pm.TransactionCount || 0} {t('transactions') || 'transactions'}
                              </Typography>
                              <Chip 
                                label={`${Number(pm.Percentage || 0).toFixed(1)}%`}
                                size="small"
                                sx={{ 
                                  bgcolor: 'rgba(255,255,255,0.25)',
                                  color: 'white',
                                  fontWeight: 700,
                                  fontSize: '0.7rem'
                                }}
                              />
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Daily Sales Table */}
              <Box>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {t('daily_sales_breakdown') || '📋 Daily Sales Breakdown'}
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ 
                  maxHeight: 500,
                  '& .MuiTableCell-root': {
                    borderRight: '1px solid #e0e0e0',
                    '&:last-child': {
                      borderRight: 'none'
                    }
                  }
                }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 700, borderBottom: '2px solid #bdbdbd' }}>
                          {t('date') || 'Date'}
                        </TableCell>
                        <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 700, borderBottom: '2px solid #bdbdbd' }}>
                          {t('num_documents') || 'Documents'}
                        </TableCell>
                        <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 700, borderBottom: '2px solid #bdbdbd' }}>
                          {t('total_excl_vat') || 'Total (Excl. VAT)'}
                        </TableCell>
                        <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 700, borderBottom: '2px solid #bdbdbd' }}>
                          {t('total_vat') || 'Total VAT'}
                        </TableCell>
                        <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 700, borderBottom: '2px solid #bdbdbd' }}>
                          {t('total_incl_vat') || 'Total (Incl. VAT)'}
                        </TableCell>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 700, borderBottom: '2px solid #bdbdbd' }}>
                          {t('payment_methods') || 'Payment Methods'}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.dailySales && reportData.dailySales.length > 0 ? (
                        reportData.dailySales.map((day, index) => {
                          const paymentBreakdown = day.PaymentBreakdown || day.paymentBreakdown || [];
                          const invoiceCount = day.InvoiceCount || day.invoiceCount || 0;
                          const ticketCount = day.TicketCount || day.ticketCount || 0;
                          const totalDocs = invoiceCount + ticketCount;
                          
                          return (
                            <TableRow hover key={index}>
                              <TableCell sx={{ borderBottom: '1px solid #e0e0e0' }}>
                                <Box>
                                  <Typography variant="body2" fontWeight={500}>
                                    {formatDate(day.Date || day.date)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {day.DayOfWeek || new Date(day.Date || day.date).toLocaleDateString('en-US', { weekday: 'long' })}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right" sx={{ borderBottom: '1px solid #e0e0e0' }}>
                                <Box>
                                  <Typography variant="body2" fontWeight={500}>
                                    {totalDocs}
                                  </Typography>
                                  {totalDocs > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      Invoice(s):{invoiceCount} Ticket(s):{ticketCount}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right" sx={{ borderBottom: '1px solid #e0e0e0' }}>
                                €{Number(day.TotalExclVAT || day.totalExclVAT || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ borderBottom: '1px solid #e0e0e0' }}>
                                €{Number(day.TotalVAT || day.totalVAT || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>
                                €{Number(day.TotalInclVAT || day.totalInclVAT || 0).toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ borderBottom: '1px solid #e0e0e0' }}>
                                {paymentBreakdown.length > 0 ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                    {paymentBreakdown.map((pm, pmIndex) => (
                                      <Chip
                                        key={pmIndex}
                                        label={`${pm.PaymentMethod}: €${Number(pm.Amount || 0).toFixed(2)}`}
                                        size="small"
                                        sx={{
                                          bgcolor: '#f0f2ff',
                                          color: '#667eea',
                                          fontWeight: 600,
                                          fontSize: '0.7rem',
                                          height: 20,
                                          '& .MuiChip-label': { px: 1 }
                                        }}
                                      />
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            {t('no_data_found') || 'No data found for selected period'}
                          </TableCell>
                        </TableRow>
                      )}
                      {totals && (
                        <TableRow sx={{ bgcolor: '#f5f5f5', fontWeight: 700 }}>
                          <TableCell sx={{ fontWeight: 700, borderTop: '2px solid #bdbdbd', borderBottom: '2px solid #bdbdbd' }}>
                            {t('total') || 'TOTAL'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, borderTop: '2px solid #bdbdbd', borderBottom: '2px solid #bdbdbd' }}>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {totals.totalDocs}
                              </Typography>
                              {totals.totalDocs > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  Invoice(s):{totals.invoiceCount} Ticket(s):{totals.ticketCount}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, borderTop: '2px solid #bdbdbd', borderBottom: '2px solid #bdbdbd' }}>
                            €{totals.totalExclVAT.toFixed(2)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, borderTop: '2px solid #bdbdbd', borderBottom: '2px solid #bdbdbd' }}>
                            €{totals.totalVAT.toFixed(2)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, borderTop: '2px solid #bdbdbd', borderBottom: '2px solid #bdbdbd' }}>
                            €{totals.totalInclVAT.toFixed(2)}
                          </TableCell>
                          <TableCell sx={{ borderTop: '2px solid #bdbdbd', borderBottom: '2px solid #bdbdbd' }} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}

          {/* No Data State */}
          {!reportData && !loading && (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <AssessmentIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t('no_report_generated') || 'No Report Generated'}
              </Typography>
              <Typography variant="body2">
                {t('select_filters_and_generate') || 'Select your filters and click "Generate Report" to view the daily sale summary'}
              </Typography>
            </Box>
          )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {t('close') || 'Close'}
        </Button>
        {reportData && (
          <>
            <Button
              onClick={handlePDF}
              disabled={pdfLoading}
              startIcon={pdfLoading ? <CircularProgress size={18} color="inherit" /> : <PdfIcon />}
              variant="outlined"
              color="error"
            >
              {pdfLoading ? (t('exporting') || 'Exporting...') : (t('export_pdf') || 'Export PDF')}
            </Button>
            <Button
              onClick={handlePrint}
              startIcon={<PrintIcon />}
              variant="contained"
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              {t('print_report') || 'Print Report'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DailySaleSummaryReportViewer;
